import { AUTO_LAUNCH_RULES } from "../../config/gameplay";
import type { ProfileProgress } from "../progression/levels";
import type { AutoLaunchTicketSeconds, OrbitForgeSave } from "../save/types";
import { autoLaunchBallsPerSecond, autoLaunchDurationMultiplier } from "./effects";
import { autoLaunchPrestigeDiscountMultiplier, autoLaunchTicketPrice, autoLaunchUpgradePrice } from "./pricing";
import { autoLaunchTickets, autoLaunchUpgrades, ticketStorageCount, type AutoLaunchUpgradeKind } from "./catalog";

export interface AutoLaunchTicketRowView {
  readonly seconds: AutoLaunchTicketSeconds;
  readonly label: string;
  readonly count: number;
  readonly price: number;
  readonly unlockLevel: number;
  readonly locked: boolean;
  readonly affordable: boolean;
  readonly canBuy: boolean;
  readonly canUse: boolean;
}

export interface AutoLaunchUpgradeRowView {
  readonly kind: AutoLaunchUpgradeKind;
  readonly label: string;
  readonly level: number;
  readonly maxLevel: number;
  readonly price: number;
  readonly unlockLevel: number;
  readonly locked: boolean;
  readonly maxed: boolean;
  readonly affordable: boolean;
  readonly canBuy: boolean;
  readonly nextEffect: string;
}

export interface AutoLaunchView {
  readonly unlocked: boolean;
  readonly active: boolean;
  readonly activeTicketSeconds: AutoLaunchTicketSeconds | null;
  readonly activeRemainingSeconds: number;
  readonly pausedByStorm: boolean;
  readonly storageCount: number;
  readonly storageCap: number;
  readonly autoExtendEnabled: boolean;
  readonly nextAutoExtendTicketSeconds: AutoLaunchTicketSeconds | null;
  readonly durationMultiplier: number;
  readonly ballsPerSecond: number;
  readonly prestigeDiscountPercent: number;
  readonly tickets: readonly AutoLaunchTicketRowView[];
  readonly upgrades: readonly AutoLaunchUpgradeRowView[];
}

export function buildAutoLaunchView(save: OrbitForgeSave, progress: ProfileProgress, stormActive: boolean): AutoLaunchView {
  const storageCount = ticketStorageCount(save);
  const active = save.autoLaunch.activeRemainingSeconds > 0;
  const unlocked = progress.effectiveLevel >= AUTO_LAUNCH_RULES.unlockLevel;
  const durationMultiplier = autoLaunchDurationMultiplier(save);
  const ballsPerSecond = autoLaunchBallsPerSecond(save);
  return {
    unlocked,
    active,
    activeTicketSeconds: save.autoLaunch.activeTicketSeconds,
    activeRemainingSeconds: save.autoLaunch.activeRemainingSeconds,
    pausedByStorm: active && stormActive,
    storageCount,
    storageCap: AUTO_LAUNCH_RULES.storageCap,
    autoExtendEnabled: save.autoLaunch.autoExtendEnabled,
    nextAutoExtendTicketSeconds: nextStoredTicket(save),
    durationMultiplier,
    ballsPerSecond,
    prestigeDiscountPercent: Math.round((1 - autoLaunchPrestigeDiscountMultiplier(progress)) * 100),
    tickets: autoLaunchTickets.map((ticket) => {
      const locked = progress.effectiveLevel < ticket.unlockLevel;
      const price = autoLaunchTicketPrice(ticket, save.stats[ticket.purchaseStat], progress);
      const count = save.stats[ticket.ticketStat];
      return {
        seconds: ticket.seconds,
        label: ticket.label,
        count,
        price,
        unlockLevel: ticket.unlockLevel,
        locked,
        affordable: save.stats.Credits >= price,
        canBuy: unlocked && !locked && storageCount < AUTO_LAUNCH_RULES.storageCap && save.stats.Credits >= price,
        canUse: unlocked && !locked && !active && count > 0
      };
    }),
    upgrades: autoLaunchUpgrades.map((upgrade) => {
      const level = save.stats[upgrade.levelStat];
      const locked = progress.effectiveLevel < upgrade.unlockLevel;
      const maxed = level >= upgrade.maxLevel;
      const price = autoLaunchUpgradePrice(upgrade, level);
      return {
        kind: upgrade.kind,
        label: upgrade.label,
        level,
        maxLevel: upgrade.maxLevel,
        price,
        unlockLevel: upgrade.unlockLevel,
        locked,
        maxed,
        affordable: save.stats.Credits >= price,
        canBuy: unlocked && !locked && !maxed && save.stats.Credits >= price,
        nextEffect: nextUpgradeEffect(save, upgrade.kind)
      };
    })
  };
}

function nextUpgradeEffect(save: OrbitForgeSave, kind: AutoLaunchUpgradeKind): string {
  if (kind === "duration") {
    return `x${autoLaunchDurationMultiplier({ ...save, stats: { ...save.stats, AutoLaunchDurationLevel: save.stats.AutoLaunchDurationLevel + 1 } }).toFixed(2)}`;
  }
  return `${autoLaunchBallsPerSecond({ ...save, stats: { ...save.stats, AutoLaunchRateLevel: save.stats.AutoLaunchRateLevel + 1 } }).toFixed(1)}/s`;
}

function nextStoredTicket(save: OrbitForgeSave): AutoLaunchTicketSeconds | null {
  if (save.stats.AutoLaunchTickets600 > 0) return 600;
  if (save.stats.AutoLaunchTickets300 > 0) return 300;
  if (save.stats.AutoLaunchTickets120 > 0) return 120;
  if (save.stats.AutoLaunchTickets60 > 0) return 60;
  return null;
}
